import React, { useState, useRef, useEffect } from 'react';
import { API_BASE_URL } from '../../config/api.config';
import './Chatbot.css';

const SUGGESTED_QUESTIONS = [
    'What is an SGTIN and how is it structured?',
    'Explain EPCIS ObjectEvent vs AggregationEvent',
    'What CBV business steps apply to pharma shipping?',
    'How do I encode a GLN as an SGLN?',
    'What EPCIS events are required for DSCSA compliance?',
];

const MessageBubble = ({ msg }) => {
    // Render code blocks inside message content
    const renderContent = (text) => {
        const parts = text.split(/(```[\s\S]*?```)/g);
        return parts.map((part, i) => {
            if (part.startsWith('```')) {
                const lines = part.slice(3).split('\n');
                const lang = lines[0].trim();
                const code = lines.slice(1).join('\n').replace(/```$/, '').trimEnd();
                return (
                    <pre key={i} className="chat-code-block">
                        {lang && <span className="chat-code-lang">{lang}</span>}
                        <code>{code}</code>
                    </pre>
                );
            }
            // Bold **text**
            const boldParts = part.split(/(\*\*[^*]+\*\*)/g);
            return (
                <span key={i}>
                    {boldParts.map((bp, j) =>
                        bp.startsWith('**') ? <strong key={j}>{bp.slice(2, -2)}</strong> : bp
                    )}
                </span>
            );
        });
    };

    return (
        <div className={`chat-bubble chat-bubble--${msg.role}`}>
            {msg.role === 'assistant' && (
                <div className="chat-avatar">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                </div>
            )}
            <div className="chat-bubble__content">
                {renderContent(msg.content)}
            </div>
        </div>
    );
};

const TypingIndicator = () => (
    <div className="chat-bubble chat-bubble--assistant">
        <div className="chat-avatar">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
        </div>
        <div className="chat-bubble__content chat-typing">
            <span></span><span></span><span></span>
        </div>
    </div>
);

const Chatbot = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: "Hi! I'm your GS1 & EPCIS 1.2 expert. Ask me anything about serialization standards, EPC identifiers, EPCIS events, CBV, or DSCSA compliance.",
        },
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            inputRef.current?.focus();
        }
    }, [messages, open]);

    const sendMessage = async (text) => {
        const userText = (text || input).trim();
        if (!userText || loading) return;

        setInput('');
        setError(null);

        const newMessages = [...messages, { role: 'user', content: userText }];
        setMessages(newMessages);
        setLoading(true);

        try {
            const res = await fetch(`${API_BASE_URL}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    messages: newMessages.map(m => ({ role: m.role, content: m.content })),
                }),
            });

            const data = await res.json();

            if (!data.success) {
                throw new Error(data.message || 'Request failed');
            }

            setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const clearChat = () => {
        setMessages([{
            role: 'assistant',
            content: "Hi! I'm your GS1 & EPCIS 1.2 expert. Ask me anything about serialization standards, EPC identifiers, EPCIS events, CBV, or DSCSA compliance.",
        }]);
        setError(null);
    };

    return (
        <>
            {/* Floating trigger button */}
            <button
                className={`chatbot-trigger ${open ? 'chatbot-trigger--open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-label="Open GS1 expert chat"
            >
                {open ? (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                ) : (
                    <>
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                        <span className="chatbot-trigger__label">GS1 Expert</span>
                    </>
                )}
            </button>

            {/* Chat window */}
            {open && (
                <div className="chatbot-window">
                    {/* Header */}
                    <div className="chatbot-header">
                        <div className="chatbot-header__info">
                            <div className="chatbot-header__avatar">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <div>
                                <div className="chatbot-header__name">GS1 & EPCIS Expert</div>
                                <div className="chatbot-header__sub">
                                    <span className="chatbot-header__dot"></span>
                                    Powered by Claude
                                </div>
                            </div>
                        </div>
                        <button className="chatbot-header__clear" onClick={clearChat} title="Clear conversation">
                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="chatbot-messages">
                        {messages.map((msg, i) => (
                            <MessageBubble key={i} msg={msg} />
                        ))}
                        {loading && <TypingIndicator />}
                        {error && (
                            <div className="chat-error">
                                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {error}
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Suggested questions (only on first load) */}
                    {messages.length === 1 && (
                        <div className="chatbot-suggestions">
                            {SUGGESTED_QUESTIONS.map((q, i) => (
                                <button key={i} className="chatbot-suggestion" onClick={() => sendMessage(q)}>
                                    {q}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Input */}
                    <div className="chatbot-input-row">
                        <textarea
                            ref={inputRef}
                            className="chatbot-input"
                            placeholder="Ask about GS1 standards, EPCIS events, EPC URIs…"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            rows={1}
                            disabled={loading}
                        />
                        <button
                            className="chatbot-send"
                            onClick={() => sendMessage()}
                            disabled={!input.trim() || loading}
                            aria-label="Send message"
                        >
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default Chatbot;
